<template>
  <div class="viewer-widget">
    <div class="f-w f-h">
      <!-- Diplay video ; iframe for fullscreen support -->
      <iframe
        v-if="what == 0"
        ref="media"
        :key="embedKey"
        class="media"
        :width="mediaWidth"
        :height="mediaHeight"
        :src="mediaSrc"
        frameborder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
        @load="onMediaLoad"
      />
      <!-- Diplay PDF file ; embed for browser support -->
      <embed
        v-else
        ref="media"
        :key="embedKey"
        class="media"
        :src="mediaSrc"
        :width="mediaWidth"
        :height="mediaHeight"
        v-bind="pdfViewerProps"
        @load="onMediaLoad"
      >
    </div>
    <div v-if="src == '' && !uploadFile" class="not-valid">
      {{ $t('message.WIDGET_VIEWER_MISSING_ADDRESS') }}
    </div>
    <div v-else-if="!loaded && what == 0" class="not-valid">
      {{ $t('message.WIDGET_VIEWER_LOADING') }}
    </div>
    <div v-else-if="what == 0 && !mediaSrc" class="not-valid">
      {{ $t('message.WIDGET_VIEWER_NOT_VALID') }}
    </div>
  </div>
</template>

<script>
/**
 * Media viewer widget. Allows the user see a PDF file or a video iframe
 */

import $ from 'jquery';
import embed from 'embed-video';
import { FETCH_FILE_DATA } from '@/store/modules/file/action-types';
// Mixins
import mixinWidgets from '@/mixins/widgets.vue';

export default {

  name: 'ViewerWidget',

  mixins: [mixinWidgets],

  props: {
    what: {
      type: Number,
      default: 0,
    },
    src: {
      type: String,
      default: '',
    },
    uploadFile: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      loaded: false,
      afterFileUpdated: null,
      embedKey: 1,
      mediaWidth: 0,
      mediaHeight: 0,
    };
  },

  computed: {
    config() {
      const config = [
        {
          key: 'viewerTopSeparator',
          value: '',
          component: 'Div',
          options: { class: 'filters-zone__modal__sep' },
        },
        {
          key: 'what',
          value: this.what,
          component: 'EditableLabelRadio',
          options: {
            class: 'mt5',
            legend: this.$t('message.WIDGET_VIEWER_WHAT'),
            labelKey: 'label',
            items: [
              { id: 0, label: this.$t('message.WIDGET_VIEWER_VIDEO') },
              { id: 1, label: this.$t('message.WIDGET_VIEWER_PDF') },
            ],
            genericBorder: true,
          },
        },
        {
          key: 'src',
          value: this.src,
          component: 'EditableLabel',
          options: {
            class: `viewer-widget__src mt5 ${this.uploadFile === 1 && Number(this.what) === 1 ? 'greyed-out' : ''}`,
            legend: this.$t('message.WIDGET_VIEWER_ADDRESS'),
            placeholder: this.$t('message.WIDGET_VIEWER_HTTPS'),
            useTextarea: true,
            disabled: this.uploadFile === 1 && Number(this.what) === 1,
            id: `cfg-widget${this.widget.id}`,
          },
        },
      ];
      if (Number(this.what) === 1) {
        config.push({
          key: 'uploadFile',
          value: this.uploadFile,
          component: 'IntCheckbox',
          options: {
            title: this.$t('message.WIDGET_VIEWER_UPLOAD_PDF'),
          },
        });
        config.push({
          key: 'files',
          value: this.widget.files,
          component: 'AutonomousFileChipList',
          options: {
            class: `mt5 ${this.uploadFile ? '' : 'greyed-out'}`,
            pathname: 'widgets/',
            editable: true,
            disabled: this.uploadFile !== 1,
            column: true,
            forceButtonText: true,
          },
        });
      }
      return config;
    },
    mediaSrc() {
      switch (this.what) {
        case 0:
          if (this.src) {
            const embedIframe = embed(this.appendHttp(this.src));
            return embedIframe ? $.parseHTML(embedIframe)[0].src : null;
          }
          break;
        default:
          return Number(this.uploadFile) === 1 ? this.uploadedFileUrl : this.appendHttp(this.src);
      }
      return '';
    },
    /** String: URL of file previously uploaded through file system */
    uploadedFileUrl() {
      if (this.widget.files && this.widget.files.length) {
        return this.$store.state.file.fileData[`${this.widget.files[0]}`];
      }
      return '';
    },
    /** Boolean: When using the PDF option, user may also enter an URL to display an iframe */
    isPDF() {
      if (this.what === 0) {
        return false;
      }
      if (this.uploadFile === 1) {
        return true;
      }
      return this.src.split(/[#?]/)[0].split('.').pop().trim() === 'pdf';
    },
    /** Object: props to apply to the <embed> element */
    pdfViewerProps() {
      return {
        type: this.isPDF ? 'application/pdf' : undefined,
      };
    },
  },

  watch: {
    what() {
      this.loaded = false;
    },
    src() {
      this.loaded = false;
      // This seems to be the only way to refresh the <embed> on chrome
      this.embedKey++;
    },
    'widget.files'() {
      if (this.widget.files && this.widget.files.length) {
        this.$store.dispatch(`file/${FETCH_FILE_DATA}`, { fileId: this.widget.files[0] });
      }
    },
  },

  mounted() {
    this.sizeChanged();
    if (this.widget.files && this.widget.files.length) {
      this.$store.dispatch(`file/${FETCH_FILE_DATA}`, { fileId: this.widget.files[0] });
    }
  },

  methods: {
    onMediaLoad() {
      this.loaded = true;
    },
    /** Resize the iframe / embed */
    sizeChanged() {
      if (this.$refs.media) {
        this.mediaWidth = this.$refs.media.offsetWidth;
        this.mediaHeight = this.$refs.media.offsetHeight;
      }
    },
    /** embed library doesn't support URLs w/o a prefix. embed HTMLElement tries to load www.(...) as a relative URL */
    appendHttp(url) {
      if (!/^https?:\/\//i.test(url)) {
        url = `https://${url}`;
      }
      return url;
    },
  },
};

</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.widget-component-child.viewer-widget {
  overflow: hidden;
  position: relative;

  .not-valid {
    text-align: center;
    position: absolute;
    left: 10px;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
  }
  .media {
    width: 100%;
    height: 100%;
  }
}

.viewer-widget__src {
  margin-top: 1.5rem;
}
</style>
